import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import {
  Badge, CssBaseline, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Button,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import { getSidebarItemsByRole } from './utils/navigation';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import AppRoutes from './routes/AppRoutes';
import { Link } from 'react-router-dom';
import theme from './styles/theme';
import config from './config/app.config';
import { getOpenFallEventCount } from './services/fallEventService';

const drawerWidth = 220;
const FALL_EVENT_BADGE_POLL_MS = 15000;
const FALL_EVENT_SOCKET_RETRY_MS = 3000;

const buildNotificationWebSocketUrl = (token) => {
  if (!token) {
    return null;
  }

  try {
    const apiUrl = new URL(config.api.baseURL);
    const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    const socketUrl = new URL(`${protocol}//${apiUrl.host}/ws/admin/notifications/`);
    socketUrl.searchParams.set('token', token);
    return socketUrl.toString();
  } catch (error) {
    console.error('Unable to build notification websocket URL:', error);
    return null;
  }
};

// const navItems = [
//   { label: 'Staff', path: '/staff', icon: <PeopleIcon /> },
//   { label: 'Clients', path: '/clients', icon: <GroupIcon /> },
//   { label: 'Relatives', path: '/relatives', icon: <FamilyRestroomIcon /> },
//   { label: 'Service Types', path: '/service-types', icon: <MiscellaneousServicesIcon /> },
//   { label: 'Visits', path: '/videos', icon: <EventIcon /> },
//   { label: 'Invoices', path: '/invoices', icon: <ReceiptIcon /> },
//   { label: 'Payments', path: '/payments', icon: <PaymentIcon /> },
//   { label: 'Profile', path: '/profile', icon: <PeopleIcon /> },
// ];

function PrivateLayout() {
  const { user, token, logout, navigate: navigateFromContext } = useAuth();
  const navigate = navigateFromContext || ((path) => { window.location.href = path; });
  const navItems = getSidebarItemsByRole(user?.role);
  const location = useLocation();
  const [openFallEventCount, setOpenFallEventCount] = useState(0);
  const [isFallEventSocketConnected, setIsFallEventSocketConnected] = useState(false);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const shouldShowFallEventBadge = useMemo(
    () => navItems.some((item) => item.path === '/fall-events'),
    [navItems],
  );

  const loadOpenFallEventCount = useCallback(async () => {
    if (!shouldShowFallEventBadge) {
      setOpenFallEventCount(0);
      return;
    }
    try {
      const data = await getOpenFallEventCount();
      setOpenFallEventCount(Number(data?.count) || 0);
    } catch (error) {
      console.error('Unable to load open fall event count:', error);
    }
  }, [shouldShowFallEventBadge]);

  useEffect(() => {
    if (!shouldShowFallEventBadge) {
      setOpenFallEventCount(0);
      return undefined;
    }

    loadOpenFallEventCount();
    if (isFallEventSocketConnected) {
      return undefined;
    }

    const intervalId = window.setInterval(loadOpenFallEventCount, FALL_EVENT_BADGE_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [isFallEventSocketConnected, loadOpenFallEventCount, shouldShowFallEventBadge]);

  useEffect(() => {
    if (!shouldShowFallEventBadge || !token) {
      setIsFallEventSocketConnected(false);
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      return undefined;
    }

    let isActive = true;

    const connect = () => {
      const socketUrl = buildNotificationWebSocketUrl(token);
      if (!socketUrl || !isActive) {
        return;
      }

      if (socketRef.current) {
        socketRef.current.close();
      }

      const socket = new WebSocket(socketUrl);
      socketRef.current = socket;

      socket.addEventListener('open', () => {
        if (!isActive) {
          return;
        }
        setIsFallEventSocketConnected(true);
      });

      socket.addEventListener('message', (event) => {
        if (!isActive) {
          return;
        }

        try {
          const payload = JSON.parse(event.data);
          const openCount = Number(payload?.summary?.open_fall_count);
          if (!Number.isNaN(openCount)) {
            setOpenFallEventCount(openCount);
          }
        } catch (error) {
          console.error('Unable to parse fall event websocket payload:', error);
        }
      });

      socket.addEventListener('close', () => {
        if (!isActive) {
          return;
        }
        setIsFallEventSocketConnected(false);
        reconnectTimerRef.current = window.setTimeout(connect, FALL_EVENT_SOCKET_RETRY_MS);
      });

      socket.addEventListener('error', (error) => {
        console.error('Fall event websocket error:', error);
      });
    };

    connect();

    return () => {
      isActive = false;
      setIsFallEventSocketConnected(false);
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [shouldShowFallEventBadge, token]);

  const sidebarItems = useMemo(
    () => navItems.map((item) => (
      item.path === '/fall-events'
        ? { ...item, badgeCount: openFallEventCount }
        : item
    )),
    [navItems, openFallEventCount],
  );

  const handleLogout = async () => {
    // await so the refresh token gets blacklisted server-side
    // BEFORE we navigate away and discard the React tree.
    await logout();
    navigate('/login', { replace: true });
  };

  const handleDashboardRedirect = () => {
    switch (user?.role) {
      case 'admin':
        navigate('/dashboard');
        break;
      case 'client':
        navigate('/client-dashboard');
        break;
      case 'nurse':
        navigate('/nurse-dashboard');
        break;
      case 'carer':
        navigate('/carer-dashboard');
        break;
      default:
        navigate('/profile');
    }
  };
  console.log('AUTH USER:', user);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" color="default" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button
            color="primary"
            onClick={handleDashboardRedirect}
            sx={{
              fontSize: 18,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            Delight Supported Living
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {user?.username && (
              <Typography variant="body1" color="text.primary">
                Welcome, {user.username}
              </Typography>
            )}
            <Button variant="outlined" color="primary" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            background: 'linear-gradient(180deg, rgba(122, 59, 193, 0.08) 0%, rgba(122, 59, 193, 0) 100%)',
            backdropFilter: 'blur(16px)',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {sidebarItems.map((item) => (
              <ListItem
                button
                key={item.label}
                component={Link}
                to={item.path}
                selected={location.pathname.startsWith(item.path)}
                sx={{
                  borderRadius: 12,
                  mx: 1.5,
                  mb: 0.5,
                  transition: 'background 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    bgcolor: 'rgba(122,59,193,0.08)',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(122,59,193,0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(122,59,193,0.18)',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
                {typeof item.badgeCount === 'number' && item.badgeCount > 0 && (
                  <Badge
                    badgeContent={item.badgeCount}
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: 0,
                      },
                    }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: { xs: 2.5, sm: 4 },
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <AppRoutes isPrivate />
      </Box>
    </Box>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  if (loading) return <Typography>Loading...</Typography>;
  return user ? <PrivateLayout /> : <AppRoutes />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
